import React, { useEffect, useState } from "react";
import { Loader2, Eye } from "lucide-react";
import { api } from "@/lib/api";

// Lot 612 — aperçu AVANT activation d'un mode d'affichage du fil : sur les membres actifs (30 j), combien verraient
// le repli (fil complet + carte « Suis des membres »), un fil filtré, et parmi eux un fil « presque vide ».
const Tile = ({ label, value, sub, tone, testId }) => (
  <div className="rounded-xl px-3 py-2 border" style={{ borderColor: tone || "var(--yalola-border)" }} data-testid={testId} data-value={value}>
    <div className="text-xl font-bold font-display" style={tone ? { color: tone } : undefined}>{value}</div>
    <div className="text-[11px] leading-tight" style={{ color: "var(--yalola-text-muted)" }}>{label}</div>
    {sub && <div className="text-[10px] mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>{sub}</div>}
  </div>
);

export const FeedModePreview = ({ mode, minFollows }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const mf = Math.max(0, Math.min(100, Number(minFollows ?? 5) || 0));

  useEffect(() => {
    if (!mode || mode === "everyone") { setData(null); return undefined; }
    let alive = true;
    setLoading(true);
    setError(false);
    const timer = setTimeout(() => {
      api.get("/admin/feed-mode/preview", { params: { mode, min_follows: mf } })
        .then(({ data }) => { if (alive) setData(data); })
        .catch(() => { if (alive) setError(true); })
        .finally(() => { if (alive) setLoading(false); });
    }, 500);
    return () => { alive = false; clearTimeout(timer); };
  }, [mode, mf]);

  if (!mode || mode === "everyone") return null;
  const est = data?.estimated || data;
  const ratio = data && data.filtered ? data.near_empty / data.filtered : 0;
  const tone = !data || !data.near_empty ? undefined : ratio >= 0.2 ? "#B91C1C" : "#B45309";

  return (
    <div className="rounded-xl p-3 space-y-2" style={{ background: "var(--yalola-surface-alt)" }} data-testid="feed-mode-preview" data-state={loading ? "loading" : error ? "error" : data ? "ready" : "idle"}>
      <div className="flex items-center gap-2 text-sm font-medium">
        <Eye size={14} style={{ color: "var(--yalola-primary)" }} />
        <span>Aperçu avant activation — membres actifs ({data?.active_days ?? 30} derniers jours)</span>
        {loading && <Loader2 size={13} className="animate-spin ml-auto" data-testid="feed-mode-preview-loading" />}
      </div>
      {error ? (
        <p className="text-xs" style={{ color: "#B91C1C" }} data-testid="feed-mode-preview-error">Aperçu indisponible pour le moment.</p>
      ) : data ? (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <Tile label="membres actifs" value={data.active_total} testId="feed-mode-preview-active" />
            <Tile label="verraient le fil complet (repli : suivent moins de comptes que le seuil)" value={est.fallback} testId="feed-mode-preview-fallback" />
            <Tile label="verraient un fil filtré" value={est.filtered} testId="feed-mode-preview-filtered" />
            <Tile label={`dont un fil presque vide (< ${data.threshold} publications visibles sur ${data.window_days} j)`} value={est.near_empty} tone={tone} testId="feed-mode-preview-near-empty" />
          </div>
          <p className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }} data-testid="feed-mode-preview-note">
            {data.posts_recent} publication(s) publique(s) sur les {data.window_days} derniers jours.
            {data.sampled ? ` Estimation à partir d'un échantillon de ${data.sample_size} membres les plus récents.` : ""}
            {data.near_empty > 0 ? " Conseil : augmente le seuil de repli ou choisis « Abonnements d'abord » pour que personne ne voie un fil vide." : " Aucun membre ne verrait un fil vide avec ce réglage."}
          </p>
        </>
      ) : (
        <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Calcul en cours…</p>
      )}
    </div>
  );
};

export default FeedModePreview;
